import React, { useState } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Globe,
  Languages,
  LogOut,
  Share2,
  Copy,
  X,
  FileText,
  Shield,
  ArrowLeft,
  Plus,
  Minus,
  Pencil,
} from "lucide-react";
import { FaWhatsapp, FaTelegram } from "react-icons/fa";
import Settings from "screens/Dashboard/Settings/Settings";
import { useNavigate } from "react-router-dom";

// Terms of Use Page Component
export const TermsOfUsePage = ({ onBack, onSwitchToPrivacy }) => {
  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 md:p-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          <div className="flex items-center gap-2 text-[#3B0069] hover:text-[#58267F] mb-4">
            <button onClick={onBack} className="flex items-center gap-2">
              <ArrowLeft size={20} />
              <span>Back to Profile</span>
            </button>
          </div>

          <div className="text-center">
            <h1 className="text-3xl font-extrabold text-[#3B0069] mb-2">
              Terms and Conditions
            </h1>
            <p className="text-sm text-gray-500">Please read carefully</p>
          </div>

          {/* Tabs-like inline nav */}
          <div className="mt-6 flex items-center justify-center gap-6">
            {onSwitchToPrivacy ? (
              <button
                onClick={onSwitchToPrivacy}
                className="text-sm font-medium text-gray-500 hover:text-[#3B0069] pb-1"
                aria-label="View Privacy Statement"
              >
                Privacy Statement
              </button>
            ) : (
              <span className="text-sm font-medium text-gray-400 pb-1">
                Privacy Statement
              </span>
            )}
            <div className="text-sm font-medium text-[#3B0069] border-b-2 border-[#3B0069] pb-1">
              Terms and Conditions
            </div>
          </div>

          <div className="space-y-6 text-gray-700 mt-6">
            <p>
              These Terms of Use ("Terms") govern your use of the SiteMaster.ai
              platform, including its website (https://www.sitemaster.ai) and
              associated software services ("Platform"), operated by
              SiteMaster.ai hereinafter referred to as ("we", "us", "our"). By
              accessing or using the Platform, you agree to comply with and be
              bound by these Terms. If you do not agree, please refrain from
              using the Platform.
            </p>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                1. Eligibility
              </h2>
              <p>
                You must be at least 18 years of age and have the authority to
                enter into a binding contract in order to use the Platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                2. Account Registration
              </h2>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>
                  You must register for an account to access most features.
                </li>
                <li>
                  You are responsible for maintaining the confidentiality of
                  your login credentials.
                </li>
                <li>
                  You agree to provide accurate, complete, and updated
                  information.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                3. Platform Usage
              </h2>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>
                  The Platform is designed for real estate and construction
                  management purposes.
                </li>
                <li>
                  You agree not to misuse the Platform or engage in
                  unauthorized, unlawful, or harmful activities.
                </li>
                <li>
                  You agree not to reverse engineer, decompile, or otherwise
                  tamper with the Platform's source code.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                4. Intellectual Property
              </h2>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>
                  All content, software, trademarks, and proprietary information
                  on the Platform are the intellectual property of
                  SiteMaster.ai.
                </li>
                <li>
                  You may not reproduce, distribute, or create derivative works
                  without our prior written consent.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                5. User Content
              </h2>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>
                  You retain ownership of content you upload, but grant us a
                  license to use, store, and display such content for
                  operational purposes.
                </li>
                <li>
                  You are responsible for ensuring your content does not violate
                  applicable laws or third-party rights.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                6. Payments and Subscriptions
              </h2>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>
                  Certain features may require payment. Fees, billing terms, and
                  subscription models will be disclosed at the time of purchase.
                </li>
                <li>
                  All payments are processed through secure third-party
                  gateways. We do not store financial data.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                7. Third-Party Services
              </h2>
              <p>
                The Platform may integrate with third-party tools or services.
                We are not responsible for the content, privacy, or practices of
                these third-party providers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                8. Termination
              </h2>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>
                  We reserve the right to suspend or terminate your access to
                  the Platform at our discretion, especially in case of
                  violations of these Terms.
                </li>
                <li>
                  You may delete your account at any time by contacting us.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                9. Disclaimers and Limitation of Liability
              </h2>
              <p className="mb-2">
                The Platform is provided on an 'as-is' and 'as-available' basis.
              </p>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>We make no warranties of performance or accuracy.</li>
                <li>
                  To the extent permitted by law, we are not liable for any
                  indirect, incidental, or consequential damages.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                10. Indemnity
              </h2>
              <p>
                You agree to indemnify and hold harmless SiteMaster.ai from any
                claims, liabilities, losses, or expenses arising out of your use
                of the Platform or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                11. Governing Law and Dispute Resolution
              </h2>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>
                  These Terms are governed by the laws of the Republic of India.
                </li>
                <li>
                  Any disputes shall be subject to the exclusive jurisdiction of
                  the courts in Kolkata.
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                12. Amendments
              </h2>
              <p>
                We may revise these Terms from time to time. Continued use of
                the Platform indicates your acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                13. Contact Us
              </h2>
              <p>For any questions or concerns, please contact us at:</p>
              <p className="mt-2">
                <strong>Email:</strong> contact@sitemaster.ai
              </p>
              <p>
                <strong>Address:</strong> 172 FD Block, Saltlake City, Sec -III,
                Bidhannagar, Kolkata, West Bengal 700106, IN
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Page Component
export const PrivacyPolicyPage = ({ onBack }) => {
  const [showTermsLocal, setShowTermsLocal] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  const toggleItem = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  if (showTermsLocal) {
    return (
      <TermsOfUsePage
        onBack={() => setShowTermsLocal(false)}
        onSwitchToPrivacy={() => setShowTermsLocal(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 md:p-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          <div className="flex items-center gap-2 text-[#3B0069] hover:text-[#58267F] mb-4">
            <button onClick={onBack} className="flex items-center gap-2">
              <ArrowLeft size={20} />
              <span>Back to Profile</span>
            </button>
          </div>

          <div className="text-center">
            <h1 className="text-3xl font-extrabold text-[#3B0069] mb-2">
              Privacy Statement
            </h1>
            <p className="text-sm text-gray-500">Effective Date: 20.05.2025</p>
          </div>

          {/* Tabs-like inline nav */}
          <div className="mt-6 flex items-center justify-center gap-6">
            <div className="text-sm font-medium text-[#3B0069] border-b-2 border-[#3B0069] pb-1">
              Privacy Statement
            </div>
            <button
              onClick={() => setShowTermsLocal(true)}
              className="text-sm font-medium text-gray-500 hover:text-[#3B0069] pb-1"
              aria-label="View Terms and Conditions"
            >
              Terms and Conditions
            </button>
          </div>

          {/* Intro paragraphs */}
          <div className="mt-6 space-y-4 text-gray-700 leading-relaxed">
            <p>
              Welcome to SiteMaster.ai hereinafter referred to as ("we", "us"
              and "our"). This Privacy Policy explains how we collect, use,
              disclose, and protect your personal data when you use our
              platform, including our website (https://www.sitemaster.ai) and
              associated services (collectively, the "Platform").
            </p>
            <p>
              By using our Platform, you consent to the terms of this Privacy
              Policy.
            </p>
          </div>

          {/* Accordion */}
          <div className="mt-8 divide-y divide-gray-200 rounded-lg border border-gray-200">
            {[
              {
                title:
                  "Collecting and Using Personal and Anonymous Information",
                content: (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 mb-2">
                        Personal Information
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        <li>Name, email address, mobile number</li>
                        <li>Designation, company name</li>
                        <li>User ID, login credentials</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 mb-2">
                        Business Information
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        <li>Project names, contractor details, site data</li>
                        <li>Uploaded documents and reports</li>
                        <li>Communications, tasks, and schedules</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 mb-2">
                        Device & Usage Data
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        <li>IP address, browser type, operating system</li>
                        <li>Date/time of access, usage logs, clicks</li>
                        <li>Cookies, analytics data</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 mb-2">
                        Payment Information
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        <li>
                          Limited financial details (via secure third-party
                          gateway)
                        </li>
                        <li>Transaction records (non-sensitive)</li>
                      </ul>
                    </div>
                  </div>
                ),
              },
              {
                title: "Types of Information We Collect",
                content: (
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>
                      Personal, business, device, usage, and limited payment
                      data
                    </li>
                  </ul>
                ),
              },
              {
                title: "How We Collect Information",
                content: (
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Directly from user inputs and uploaded documents</li>
                    <li>Automatically via device, cookies, and analytics</li>
                    <li>From authorized users within your organization</li>
                  </ul>
                ),
              },
              {
                title: "How We Use Your Information",
                content: (
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Facilitate project tracking and task assignments</li>
                    <li>Communicate updates, alerts, and service messages</li>
                    <li>Provide support and address queries</li>
                    <li>Improve functionality and user experience</li>
                    <li>Comply with legal obligations</li>
                    <li>Create and manage your account</li>
                  </ul>
                ),
              },
              {
                title: "Third Party Data Collection",
                content: (
                  <p className="text-gray-700">
                    We may engage service providers for hosting, analytics, and
                    payments. These providers process data on our behalf under
                    strict agreements.
                  </p>
                ),
              },
              {
                title: "Your Controls and Choices",
                content: (
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>
                      Request access, correction, deletion, or portability
                    </li>
                    <li>
                      Withdraw consent where processing is based on consent
                    </li>
                    <li>Manage cookie preferences through your browser</li>
                  </ul>
                ),
              },
              {
                title: "How We Use and Share Information",
                content: (
                  <div className="space-y-2">
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>Authorized organizational users</li>
                      <li>
                        Service providers (cloud, analytics, payment gateway)
                      </li>
                      <li>Law enforcement or regulators where required</li>
                      <li>Investors/acquirers during business transactions</li>
                    </ul>
                    <p className="text-gray-700 font-medium">
                      Note: We do not sell your personal data to third parties.
                    </p>
                  </div>
                ),
              },
              {
                title: "Legal Basis for Processing",
                content: (
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Your consent (DPDP Act, 2023)</li>
                    <li>Contractual necessity</li>
                    <li>Compliance with laws and regulations</li>
                    <li>Legitimate interests (analytics, fraud detection)</li>
                  </ul>
                ),
              },
              {
                title: "Cross-Border Data Transfers",
                content: (
                  <p className="text-gray-700">
                    Data may be processed or stored outside India using secure,
                    compliant providers. Contractual safeguards are implemented
                    where required.
                  </p>
                ),
              },
              {
                title: "Data Security",
                content: (
                  <div className="space-y-2">
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>Encryption in transit and at rest</li>
                      <li>Access controls and authentication</li>
                      <li>Regular vulnerability assessments</li>
                    </ul>
                    <p className="text-gray-700">
                      No system is entirely secure. Use strong passwords and
                      protect credentials.
                    </p>
                  </div>
                ),
              },
              {
                title: "Data Retention",
                content: (
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>For the purpose collected</li>
                    <li>As required under applicable law</li>
                    <li>For legal, accounting, or audit needs</li>
                    <li>Users may request account deletion at any time</li>
                  </ul>
                ),
              },
              {
                title: "Cookies and Tracking Technologies",
                content: (
                  <div className="space-y-2">
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li>Session management</li>
                      <li>Analytics and performance tracking</li>
                      <li>Personalization</li>
                    </ul>
                    <p className="text-gray-700">
                      You may disable cookies via browser settings;
                      functionality may be affected.
                    </p>
                  </div>
                ),
              },
              {
                title: "Grievance Officer",
                content: (
                  <div className="space-y-1 text-gray-700">
                    <p>
                      <strong>Name:</strong> Angshuman Mandal
                    </p>
                    <p>
                      <strong>Email:</strong> contact@sitemaster.ai
                    </p>
                    <p>
                      <strong>Designation:</strong> Grievance Officer
                    </p>
                    <p>
                      <strong>Response Time:</strong> Within 15 working days
                    </p>
                  </div>
                ),
              },
              {
                title: "Updates to this Policy",
                content: (
                  <p className="text-gray-700">
                    We may revise this Privacy Policy periodically. Material
                    changes will be notified via email or in-app notifications.
                  </p>
                ),
              },
              {
                title: "Contact Us",
                content: (
                  <div className="space-y-1 text-gray-700">
                    <p>
                      For queries or concerns regarding this Privacy Policy,
                      contact:
                    </p>
                    <p>
                      <strong>Email:</strong> contact@sitemaster.ai
                    </p>
                    <p>
                      <strong>Address:</strong> 172 FD Block, Saltlake City, Sec
                      -III, Bidhannagar, Kolkata, West Bengal 700106, IN
                    </p>
                  </div>
                ),
              },
            ].map((item, idx) => {
              const isOpen = openIndex === idx;
              return (
                <div key={item.title} className="px-4 md:px-6">
                  <button
                    onClick={() => toggleItem(idx)}
                    className="w-full py-4 flex items-center justify-between text-left"
                    aria-expanded={isOpen}
                    aria-controls={`privacy-acc-panel-${idx}`}
                  >
                    <span className="text-base md:text-lg font-medium text-gray-900">
                      {item.title}
                    </span>
                    <span className="ml-4">
                      {isOpen ? (
                        <Minus className="text-[#3B0069]" size={20} />
                      ) : (
                        <Plus className="text-[#3B0069]" size={20} />
                      )}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      id={`privacy-acc-panel-${idx}`}
                      className="pb-6 -mt-2 text-sm md:text-base"
                    >
                      {item.content}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main ProfileCard Component
const ProfileCard = ({ data, handleLogOut }) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const navigate = useNavigate();

  const handleShareClick = () => {
    setIsShareModalOpen(true);
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopyStatus("Link copied!");
    setTimeout(() => setCopyStatus(""), 2000);
  };

  const handleShare = (platform, link) => {
    let shareUrl = "";

    switch (platform) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(link)}`;
        break;
      case "telegram":
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(
          link,
        )}&text=${encodeURIComponent("Check out SiteMaster!")}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=${encodeURIComponent(
          "Check out SiteMaster!",
        )}&body=${encodeURIComponent(link)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, "_blank");
  };

  if (showTerms) {
    return (
      <TermsOfUsePage
        onBack={() => setShowTerms(false)}
        onSwitchToPrivacy={() => {
          setShowTerms(false);
          setShowPrivacy(true);
        }}
      />
    );
  }

  if (showPrivacy) {
    return <PrivacyPolicyPage onBack={() => setShowPrivacy(false)} />;
  }

  return (
    <>
      <div className="fixed right-4 mt-2 bg-white rounded-lg shadow-xl w-96 overflow-hidden z-50">
        {/* Header Banner with Logout */}
        <div className="h-24 bg-[#3B0069] relative">
          <button
            onClick={handleLogOut}
            className="absolute top-4 right-4 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all duration-300"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>

        {/* Profile Content */}
        <div className="relative px-4 pb-4">
          {/* Profile Picture */}
          <div className="absolute -top-12 left-4">
            <div className="w-24 h-24 rounded-full bg-white p-2">
              {data?.userpic ? (
                <img
                  src={data?.userpic}
                  alt="Profile"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <div className="w-full h-full rounded-full bg-[#3B0069] flex items-center justify-center">
                  <span className="text-2xl text-white font-semibold">
                    {data?.first_name?.[0]}
                    {data?.last_name?.[0]}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Name and Basic Info */}
          <div className="ml-32 pt-4">
            <h1 className="text-xl font-bold text-gray-800">
              {data?.first_name} {data?.last_name}
            </h1>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Briefcase size={14} />
              {data?.profession?.charAt(0).toUpperCase() +
                data?.profession?.slice(1)}
            </p>
          </div>

          {/* Contact & Location Info */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-3 text-gray-600 text-sm">
              <Mail className="text-[#3B0069]" size={16} />
              <span>{data?.user_email}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 text-sm">
              <Phone className="text-[#3B0069]" size={16} />
              <span>{data?.contact_number}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 text-sm">
              <Languages className="text-[#3B0069]" size={16} />
              <span>{data?.language}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 text-sm">
              <MapPin className="text-[#3B0069]" size={16} />
              {data?.city || data?.state || data?.country || data?.pincode ? (
                <span>
                  {data?.city}
                  {data?.state ? `, ${data?.state}` : null}
                  {data?.country ? `, ${data?.country}` : null}
                  {data?.pincode ? ` - ${data?.pincode}` : null}
                </span>
              ) : (
                <span className="text-gray-400 italic">No address added</span>
              )}
              {/* <button
                onClick={() => {
                  navigate("/dashboard/settings");
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <Pencil size={14} />
              </button> */}
            </div>
            {/* <div className="flex items-center gap-3 text-gray-600 text-sm">
              <Globe className="text-[#3B0069]" size={16} />
              <span>
                {data?.city}
                {data?.state ? `, ${data?.state}` : null}
                {data?.country ? `, ${data?.country}` : null}
                {data?.pincode ? ` - ${data?.pincode}` : null}
              </span>
            </div> */}
            {/* <div className="flex items-center gap-3 text-gray-600 text-sm">
              <User className="text-[#3B0069]" size={16} />
              <span>{data?.gender === "M" ? "Male" : "Female"}</span>
            </div> */}

            {/* Share and Invite */}
            <div
              className="flex items-center gap-3 text-gray-600 text-sm hover:bg-gray-50 cursor-pointer p-2 rounded-lg transition-colors mr-2"
              onClick={handleShareClick}
            >
              <Share2 className="text-[#3B0069] -mt-1 -ml-2" size={16} />
              <span className="-mt-1">Share and Invite</span>
            </div>

            {/* Privacy Policy Button */}
            <div
              className="flex items-center gap-3 text-gray-600 text-sm hover:bg-gray-50 cursor-pointer p-1 rounded-lg transition-colors"
              onClick={() => setShowPrivacy(true)}
            >
              <Shield className="text-[#3B0069] -mt-1.5 -ml-1" size={16} />
              <span className="-mt-1.5">Privacy Policy</span>
            </div>

            {/* Terms of Use Button */}
            <div
              className="flex items-center gap-3 text-gray-600 text-sm hover:bg-gray-50 cursor-pointer p-1 rounded-lg transition-colors"
              onClick={() => setShowTerms(true)}
            >
              <FileText className="text-[#3B0069] -mt-1.5 -ml-1" size={16} />
              <span className="-mt-1">Terms of Use</span>
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {isShareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Share SiteMaster
              </h2>
              <button
                onClick={() => setIsShareModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Web App Link */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-700">Web Application</h3>
                  <button
                    onClick={() =>
                      handleCopyLink("https://login.sitemaster.ai")
                    }
                    className="text-[#3B0069] hover:text-[#58267F] p-1 rounded-md hover:bg-[#ECE6F0]"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  https://login.sitemaster.ai
                </p>
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() =>
                      handleShare("whatsapp", "https://login.sitemaster.ai")
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <FaWhatsapp className="h-4 w-4 text-[#58267F]" />
                  </button>
                  <button
                    onClick={() =>
                      handleShare("telegram", "https://login.sitemaster.ai")
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <FaTelegram className="h-4 w-4 text-[#58267F]" />
                  </button>
                  <button
                    onClick={() =>
                      handleShare("email", "https://login.sitemaster.ai")
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <Mail className="h-4 w-4 text-[#58267F]" />
                  </button>
                </div>
              </div>

              {/* Play Store App Link */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-700">Android App</h3>
                  <button
                    onClick={() =>
                      handleCopyLink(
                        "https://play.google.com/store/apps/details?id=com.sitemaster.sitemasterapp&pcampaignid=web_share",
                      )
                    }
                    className="text-[#3B0069] hover:text-[#58267F] p-1 rounded-md hover:bg-[#ECE6F0]"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  https://play.google.com/store/apps/details?id=com.sitemaster.sitemasterapp&pcampaignid=web_share
                </p>
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() =>
                      handleShare(
                        "whatsapp",
                        "https://play.google.com/store/apps/details?id=com.sitemaster.sitemasterapp&pcampaignid=web_share",
                      )
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <FaWhatsapp className="h-4 w-4 text-[#58267F]" />
                  </button>
                  <button
                    onClick={() =>
                      handleShare(
                        "telegram",
                        "https://play.google.com/store/apps/details?id=com.sitemaster.sitemasterapp&pcampaignid=web_share",
                      )
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <FaTelegram className="h-4 w-4 text-[#58267F]" />
                  </button>
                  <button
                    onClick={() =>
                      handleShare(
                        "email",
                        "https://play.google.com/store/apps/details?id=com.sitemaster.sitemasterapp&pcampaignid=web_share",
                      )
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <Mail className="h-4 w-4 text-[#58267F]" />
                  </button>
                </div>
              </div>

              {/* iOS App Link */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-700">iOS App</h3>
                  <button
                    onClick={() =>
                      handleCopyLink(
                        "https://apps.apple.com/us/app/sitemaster/id6740003804",
                      )
                    }
                    className="text-[#3B0069] hover:text-[#58267F] p-1 rounded-md hover:bg-[#ECE6F0]"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  https://apps.apple.com/us/app/sitemaster/id6740003804
                </p>
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() =>
                      handleShare(
                        "whatsapp",
                        "https://apps.apple.com/us/app/sitemaster/id6740003804",
                      )
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <FaWhatsapp className="h-4 w-4 text-[#58267F]" />
                  </button>
                  <button
                    onClick={() =>
                      handleShare(
                        "telegram",
                        "https://apps.apple.com/us/app/sitemaster/id6740003804",
                      )
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <FaTelegram className="h-4 w-4 text-[#58267F]" />
                  </button>
                  <button
                    onClick={() =>
                      handleShare(
                        "email",
                        "https://apps.apple.com/us/app/sitemaster/id6740003804",
                      )
                    }
                    className="p-2 bg-[#ECE6F0] rounded-full hover:bg-[#DED6E4]"
                  >
                    <Mail className="h-4 w-4 text-[#58267F]" />
                  </button>
                </div>
              </div>

              {copyStatus && (
                <div className="bg-green-50 text-green-600 text-sm py-2 px-3 rounded mt-2 text-center">
                  {copyStatus}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileCard;
